import React from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import SEO from '../../components/SEO';
import { gearApiClient } from '../../gearApiClient';
import { gearIcons, getGearLabel } from '../../gears';
import type { GearItem } from '../../models/GearItem';
import Inventory2Icon from '@mui/icons-material/Inventory2';

type GearCatalogItemViewModel = GearItem & {
  inventoryId?: string;
  inventoryName?: string;
  organizationName?: string;
  addressLine1?: string;
  addressLine2?: string | null;
  city?: string;
  zipCode?: string | null;
  countryCode?: string;
  returnByTime?: string;
};

function getItemInventory(item: GearCatalogItemViewModel): GearItem['inventory'] {
  if (item.inventory) {
    return item.inventory;
  }

  return {
    id: item.inventoryId ?? '',
    organizationName: item.organizationName ?? '',
    name: item.inventoryName ?? '',
    addressLine1: item.addressLine1 ?? '',
    addressLine2: item.addressLine2 ?? null,
    city: item.city ?? '',
    zipCode: item.zipCode ?? null,
    countryCode: item.countryCode ?? '',
    returnByTime: item.returnByTime,
  };
}

const DATE_FORMAT = 'YYYY-MM-DD';

function formatDate(date: Dayjs | null): string | undefined {
  return date && date.isValid() ? date.format(DATE_FORMAT) : undefined;
}

function formatCurrency(amount: number, currencyCode: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 2,
  }).format(amount);
}

function GearItemCard({ item, href, showAvailability }: { item: GearCatalogItemViewModel; href: string; showAvailability: boolean }) {
  const { t } = useTranslation();
  const icon = gearIcons[item.gearType] ?? <Inventory2Icon fontSize="small" />;
  const itemInventory = getItemInventory(item);

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        borderColor: 'divider',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <CardActionArea component={Link} to={href} sx={{ height: '100%', alignItems: 'stretch' }}>
        <CardContent sx={{ height: '100%' }}>
          <Stack spacing={1.5} sx={{ height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '12px',
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                  }}
                >
                  {icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {getGearLabel(item.gearType)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {itemInventory.organizationName}
                  </Typography>
                </Box>
              </Stack>
              {showAvailability && item.available ? <Chip size="small" color="success" label={t('gearCatalog.available', 'Available')} /> : null}
            </Stack>

            {item.details ? (
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                {item.details}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                {t('gearCatalog.noDetails', 'No additional details')}
              </Typography>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" label={itemInventory.city} />
              <Chip size="small" variant="outlined" label={formatCurrency(item.pricePerDay, item.currencyCode) + ' / ' + t('gearCatalog.perDay', 'day')} />
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function GearCatalogPage() {
  const { t } = useTranslation();
  const gearTypes = React.useMemo(() => Object.keys(gearIcons).sort((left, right) => getGearLabel(left).localeCompare(getGearLabel(right))), []);
  const [selectedGearType, setSelectedGearType] = React.useState('');
  const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(null);
  const [items, setItems] = React.useState<GearCatalogItemViewModel[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const hasValidDateRange = Boolean(startDate && endDate && endDate.isAfter(startDate, 'day'));

  const detailSearch = React.useMemo(() => {
    const params = new URLSearchParams();
    if (hasValidDateRange) {
      const formattedStart = formatDate(startDate);
      const formattedEnd = formatDate(endDate);
      if (formattedStart && formattedEnd) {
        params.set('startDate', formattedStart);
        params.set('endDate', formattedEnd);
      }
    }
    const query = params.toString();
    return query ? `?${query}` : '';
  }, [hasValidDateRange, startDate, endDate]);

  React.useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const loadGearItems = async () => {
        setLoading(true);
        setError('');

        try {
          const filters: Record<string, string> = {};
          if (selectedGearType) {
            filters.gearType = selectedGearType;
          }
          if (hasValidDateRange) {
            const formattedStart = formatDate(startDate);
            const formattedEnd = formatDate(endDate);
            if (formattedStart && formattedEnd) {
              filters.startDate = formattedStart;
              filters.endDate = formattedEnd;
            }
          }

          const response = await gearApiClient.getGearItems(filters);
          if (!cancelled) {
            setItems(response);
          }
        } catch (err) {
          if (!cancelled) {
            setItems([]);
            setError(t('gearCatalog.loadError', 'Failed to load gear catalog'));
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

      void loadGearItems();
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [selectedGearType, hasValidDateRange, startDate, endDate, t]);

  return (
    <>
      <SEO
        title={t('gearCatalog.seoTitle', 'Rent gear')}
        description={t('gearCatalog.seoDescription', 'Browse available rehearsal gear across all organizations.')}
      />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography component="h1" variant="h4" fontWeight={800} gutterBottom>
              {t('gearCatalog.title', 'Rent gear')}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
              {t('gearCatalog.subtitle', 'Filter the public catalog by gear type and date range.')}
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {t('gearCatalog.filterByType', 'Filter by gear type')}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip
                    label={t('gearCatalog.allTypes', 'All types')}
                    clickable
                    color={selectedGearType === '' ? 'primary' : 'default'}
                    variant={selectedGearType === '' ? 'filled' : 'outlined'}
                    onClick={() => setSelectedGearType('')}
                  />
                  {gearTypes.map((gearType) => (
                    <Chip
                      key={gearType}
                      label={getGearLabel(gearType)}
                      clickable
                      color={selectedGearType === gearType ? 'primary' : 'default'}
                      variant={selectedGearType === gearType ? 'filled' : 'outlined'}
                      onClick={() => setSelectedGearType(gearType)}
                    />
                  ))}
                </Stack>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <DatePicker
                    label={t('gearCatalog.startDate', 'Start date')}
                    value={startDate}
                    onChange={(value) => setStartDate(value)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                  <DatePicker
                    label={t('gearCatalog.endDate', 'End date')}
                    value={endDate}
                    onChange={(value) => setEndDate(value)}
                    minDate={startDate ?? undefined}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Stack>
              </LocalizationProvider>
            </Stack>
          </Paper>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {t('gearCatalog.emptyTitle', 'No gear found')}
              </Typography>
              <Typography color="text.secondary">
                {t('gearCatalog.emptyBody', 'Try adjusting the gear type or date filters.')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2.5}>
              {items.map((item) => (
                <Grid key={item.id} item xs={12} sm={6} md={4}>
                  <GearItemCard item={item} href={`/rent-gear/${item.id}${detailSearch}`} showAvailability={hasValidDateRange} />
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </Container>
    </>
  );
}